import React, { createContext, useContext, useEffect, useState } from "react";
import { editableContent } from "@/content/editableContent";

export const LANGS = [
  { code: "da", label: "DA", name: "Dansk" },
  { code: "en", label: "EN", name: "English" },
  { code: "es", label: "ES", name: "Español" },
];

// Brand assets — configurable image URLs in editableContent.js
export const ASSETS = editableContent.images;
export const SECTION_IMAGES = editableContent.sectionImages;

// Organisation constants
export const ORG = {
  email: "danamarksolidaritet@gmail.com",
  instagram: "@danamark.solidaritet",
  instagramUrl: "https://www.instagram.com/danamark.solidaritet/",
  cvr: "45188981",
  city: "København, Danmark",
};

// Event constants (single source for hero, event pages, timeline)
const priceFrom = editableContent.event.priceFrom ?? 250;

export const EVENT = {
  slug: editableContent.event.slug,
  dateIso: editableContent.event.dateIso,
  time: editableContent.event.time,
  weekday: { es: "Domingo", en: "Sunday", da: "Søndag" },
  monthLong: editableContent.event.monthLong,
  city: editableContent.event.city,
  location: editableContent.event.location,
  finishArea: editableContent.event.finishArea,
  distance: editableContent.event.distance,
  priceFrom,
  priceLabel: {
    es: `desde ${priceFrom} DKK`,
    en: `from ${priceFrom} DKK`,
    da: `fra ${priceFrom} DKK`,
  },
  importantDates: editableContent.event.importantDates,
};

const dict = {
  es: {
    // Nav
    nav_mission: "Nuestra historia", nav_events: "Social Run", nav_impact: "Impacto",
    nav_stories: "Historias", nav_about: "Nosotros", nav_get_involved: "Únete al movimiento",
    nav_home: "Inicio", nav_register_cta: "Participa",
    nav_volunteer: "Voluntariado",
    donate: "Donar", donate_now: "Donar ahora", register: "Inscribirse",
    nav_raffle_cta: "Comprar papeletas",
    nav_raffle: "RIFA", nav_raffle_sub: "GANA PREMIOS",
    // Hero
    hero_eyebrow: "DESDE DINAMARCA, CON SOLIDARIDAD",
    hero_title: "Cuando España nos necesita, estamos juntos.",
    hero_sub: "Desde Copenhague hasta España, la solidaridad encuentra su camino. DANAmark Solidaritet conecta a personas, comunidades y empresas para transformar la solidaridad en ayuda real cuando España más lo necesita.",
    cta_participa: "Participa",
    cta_conoce: "Nuestra historia",
    // Event info bar
    bar_date: "Domingo 6 de septiembre",
    bar_place: "Fælledparken",
    bar_dist: "5 km",
    bar_music: "Música en directo",
    bar_food: "Gastronomía española",
    bar_charity: "Evento benéfico",
    // Nuestra Historia
    section_historia_kicker: "NUESTRA HISTORIA",
    historia_headline: "Todo empezó con una decisión.",
    historia_body_1: "En otoño de 2024, las inundaciones provocadas por la DANA dejaron miles de familias afectadas en Valencia. Aunque estábamos a más de dos mil kilómetros de distancia, sentimos que no podíamos quedarnos de brazos cruzados.",
    historia_body_2: "Un grupo de voluntarios españoles y daneses decidió organizar eventos solidarios en Copenhague. Así nació DANAmark Solidaritet.",
    historia_body_3: "Lo que comenzó como una respuesta urgente se convirtió en un movimiento que demuestra que la solidaridad no entiende de fronteras.",
    // Lo que conseguimos
    section_conseguimos: "LO QUE CONSEGUIMOS JUNTOS",
    card1_title: "Más de 200.000 DKK recaudados.",
    card1_body: "Gracias a la solidaridad de cientos de personas y empresas en Dinamarca.",
    card2_title: "Eventos solidarios y subastas benéficas.",
    card2_body: "Conciertos, actividades culturales y subastas que unieron a la comunidad para una misma causa.",
    card3_title: "Un camión de ayuda enviado a Valencia.",
    card3_body: "Organizamos la recogida y el envío de mobiliario y materiales para las personas afectadas por la DANA.",
    card4_title: "Una comunidad que sigue creciendo.",
    card4_body: "Cada voluntario, colaborador y patrocinador demuestra que la solidaridad no entiende de fronteras.",
    // Hoy volvemos
    section_hoy: "HOY VOLVEMOS A CORRER POR ESPAÑA",
    hoy_body: "En 2026 los incendios forestales han devastado miles de hectáreas y obligado a muchas familias a abandonar sus hogares. Queremos volver a demostrar que la solidaridad puede viajar más rápido que cualquier frontera.",
    hoy_icon_1: "Cada inscripción.",
    hoy_icon_2: "Cada patrocinador.",
    hoy_icon_3: "Cada voluntario.",
    hoy_icon_4: "Cada donación.",
    hoy_final: "Se convierte en ayuda real.",
    // Social Run
    run_kicker: "NUESTRO EVENTO",
    run_title: "Social Run for Spain",
    run_body: "Una carrera abierta a todos. No importa la velocidad. Importa correr juntos. Terminaremos la jornada en Dansepladsen con música en directo, gastronomía española y una gran celebración de la solidaridad.",
    run_not_race: "No es una carrera. Es una celebración de la solidaridad.",
    btn_inscribirse: "Inscribirse",
    btn_mas_info: "Más información",
    event_fecha: "Fecha", event_lugar: "Lugar",
    event_distancia: "Distancia", event_precio: "Precio",
    // Timeline
    timeline_kicker: "PROGRAMA DEL DÍA",
    timeline_title: "El día que corremos juntos.",
    tl_1330: "Apertura de inscripciones", tl_1330_sub: "Acreditación y recogida de dorsales",
    tl_1430: "Calentamiento", tl_1430_sub: "Preparamos el cuerpo · todos juntos",
    tl_1500: "Salida — Social Run 5 km", tl_1500_sub: "Fælledparken, Copenhague",
    tl_1600: "Meta en Dansepladsen", tl_1600_sub: "Nos encontramos al final del recorrido",
    tl_1615: "Música en directo", tl_1615_sub: "Comienza la celebración",
    tl_1700: "Gastronomía española", tl_1700_sub: "Comida y bebida para todos",
    tl_1800: "Programa solidario", tl_1800_sub: "Historias, agradecimientos, subasta",
    tl_2200: "Cierre del evento", tl_2200_sub: "Gracias por hacerlo posible",
    // Impact counter
    counter_kicker: "NUESTRO IMPACTO",
    counter_title: "Cifras que son personas.",
    counter_raised: "DKK recaudados", counter_participants: "Participantes",
    counter_volunteers: "Voluntarios", counter_partners: "Patrocinadores",
    // Partners
    partners_kicker: "GRACIAS A", partners_title: "Quienes hacen posible el movimiento.",
    partners_main: "Socios principales",
    partners_supporting: "Socios colaboradores",
    partners_community: "Socios comunitarios",
    partners_media: "Medios",
    partners_become: "¿Quieres unirte?",
    partners_become_cta: "Contactar con patrocinios",
    partners_soon: "Próximamente",
    // Map / Event guide
    map_kicker: "GUÍA DEL EVENTO",
    map_title: "Todo lo que necesitas saber, sobre el terreno.",
    map_1: "Zona de salida", map_2: "Recorrido 5 km", map_3: "Meta",
    map_4: "Dansepladsen", map_5: "Zona gastronómica", map_6: "Escenario",
    map_7: "Sponsor village", map_8: "Aseos", map_9: "Punto de información",
    map_10: "Primeros auxilios",
    // Historias
    stories_title: "Historias de solidaridad",
    stories_kicker: "HISTORIAS",
    story_1_t: "Preparativos de la Social Run",
    story_1_e: "Entre bastidores, la ruta, los equipos y los últimos detalles de la jornada.",
    story_2_t: "Nuevos patrocinadores",
    story_2_e: "Empresas de Dinamarca y España que se suman este año al movimiento.",
    story_3_t: "Nuestros voluntarios",
    story_3_e: "Las personas que sostienen cada evento — sus rostros, sus motivos.",
    story_4_t: "El camión solidario hacia Valencia",
    story_4_e: "Una nevada en Copenhague, un remolque lleno, y familias esperando en Valencia.",
    story_5_t: "La subasta de camisetas",
    story_5_e: "Camisetas firmadas por leyendas del fútbol para una causa mayor.",
    ver_todas: "Ver todas las historias",
    // Newsletter
    news_title: "Mantente conectado.",
    news_sub: "Recibe noticias, próximos eventos e historias sobre el impacto que creamos juntos.",
    news_signup: "Suscribirse", news_placeholder: "Tu correo",
    // Footer CTA
    footer_cta_title: "Juntos a través de las fronteras.",
    footer_cta_body: "Desde Dinamarca, con el corazón en España.",
    footer_cta_btn: "Únete al movimiento",
    // Floating CTA
    float_join: "Únete", float_join_mobile: "Únete",
    // Sponsor thank-you
    sponsor_thanks_kicker: "GRACIAS",
    sponsor_thanks_title: "Nos pondremos en contacto muy pronto.",
    sponsor_thanks_body: "Mientras tanto, descarga nuestro Partnership Book para conocer las oportunidades, alcance y activaciones que ofrecemos.",
    sponsor_download: "Descargar Partnership Book (PDF)",
    sponsor_download_soon: "Libro disponible próximamente",
    // Meta
    ss_registered: "Corredores inscritos", ss_raised: "Recaudado", ss_families: "Familias apoyadas",
    trust_ssl: "Pago cifrado", trust_cvr: "Asociación registrada",
    trust_deduct: "", trust_audit: "Auditado anualmente",
    footer_tagline: "Desde Copenhague hasta España, construimos conexiones duraderas.",
    footer_links: "Enlaces", footer_legal: "Legal", footer_transparencia: "Transparencia",
    footer_contact: "Contacto", footer_closing: "Hecho con corazón en Copenhague",
    footer_donation_info: "Información sobre donaciones",
    donate_headline: "Tu donación llega — directamente.",
    donate_sub: "Sin intermediarios. 92 øre de cada corona llegan al terreno.",
    amount_label: "Elige el importe (DKK)",
    custom_amount: "Otro importe",
    monthly_soon: "Mensual — DANAmark Circle · próximamente",
    proceed: "Continuar al pago",
    thank_you_t: "Gracias — de corazón a corazón.", thank_you_sub: "Tu recibo va camino a tu bandeja de entrada.",
    read_more: "Leer más", back: "Volver",
    countdown_days: "días", countdown_hours: "horas", countdown_min: "min", countdown_sec: "s",
    admin_login: "Acceso editor", email: "Correo", password: "Contraseña", sign_in: "Entrar",
    cookie_msg: "Usamos cookies esenciales para el sitio. Tú eliges el resto.",
    cookie_accept: "Aceptar todo", cookie_essential: "Solo esenciales",
    volunteer_title: "Hazte voluntario.",
    volunteer_sub: "Nos encontramos en el movimiento. Muchas manos, un solo corazón.",
    volunteer_interests: "¿Dónde te gustaría ayudar?",
    volunteer_availability: "Tu disponibilidad",
    volunteer_message: "Cuéntanos sobre ti",
    volunteer_send: "Enviar",
    volunteer_ok: "Gracias — responderemos en 5 días laborables.",
    pay_mobilepay: "Pagar con MobilePay Box",
    pay_stripe: "Tarjeta · Apple Pay · Google Pay",
    pay_mp_pending: "MobilePay Box se activa al recibir credenciales",
    // Full-screen menu
    nav_social_run: "Social Run",
    nav_partners: "Socios",
    nav_contact: "Contacto",
    close_menu: "Cerrar",
    menu_join_run: "Inscríbete en la Social Run",
    event_quick_kicker: "NUESTRO EVENTO",
    event_line_date: "6 de septiembre de 2026",
    event_line_where: "Fælledparken · Copenhague",
    event_line_dist: "5 km Social Run",
    event_line_finish: "Meta en Dansepladsen",
    event_line_music: "Música en directo",
    event_line_food: "Gastronomía española",
    event_line_celebration: "Celebración comunitaria",
  },
  en: {
    nav_mission: "Our Story", nav_events: "Social Run", nav_impact: "Our Impact",
    nav_stories: "Stories", nav_about: "About", nav_get_involved: "Join the Movement",
    nav_home: "Home", nav_register_cta: "Register",
    nav_volunteer: "Volunteer",
    donate: "Donate", donate_now: "Donate now", register: "Register",
    nav_raffle_cta: "Buy Raffle Tickets",
    nav_raffle: "RAFFLE", nav_raffle_sub: "WIN PRIZES",
    hero_eyebrow: "FROM DENMARK, WITH SOLIDARITY",
    hero_title: "When Spain needs us, we stand together.",
    hero_sub: "From Copenhagen to Spain, solidarity finds its way. DANAmark Solidaritet connects people, communities and companies to transform solidarity into real help whenever Spain needs it most.",
    cta_participa: "Register",
    cta_conoce: "Our story",
    bar_date: "Sunday 6 September",
    bar_place: "Fælledparken",
    bar_dist: "5 km",
    bar_music: "Live music",
    bar_food: "Spanish food",
    bar_charity: "Charity event",
    section_historia_kicker: "OUR STORY",
    historia_headline: "It all began with a decision.",
    historia_body_1: "In autumn 2024, the DANA floods left thousands of families in Valencia devastated. Even from more than two thousand kilometres away, we couldn't stand by and watch.",
    historia_body_2: "A group of Spanish and Danish volunteers decided to organise charity events in Copenhagen. That is how DANAmark Solidaritet was born.",
    historia_body_3: "What began as an urgent response became a movement that proves solidarity knows no borders.",
    section_conseguimos: "WHAT WE'VE ACHIEVED TOGETHER",
    card1_title: "More than 200,000 DKK raised.",
    card1_body: "Thanks to the solidarity of hundreds of people and companies in Denmark.",
    card2_title: "Charity events and benefit auctions.",
    card2_body: "Concerts, cultural gatherings and auctions that united our community around one cause.",
    card3_title: "A truck of aid delivered to Valencia.",
    card3_body: "We organised the collection and shipment of furniture and materials for the families hit by the DANA.",
    card4_title: "A community that keeps growing.",
    card4_body: "Every volunteer, partner and sponsor proves that solidarity knows no borders.",
    section_hoy: "TODAY WE RUN FOR SPAIN AGAIN",
    hoy_body: "In 2026 the wildfires have devastated thousands of hectares and forced many families to leave their homes. We want to prove once more that solidarity travels faster than any border.",
    hoy_icon_1: "Every registration.",
    hoy_icon_2: "Every sponsor.",
    hoy_icon_3: "Every volunteer.",
    hoy_icon_4: "Every donation.",
    hoy_final: "Becomes real help.",
    run_kicker: "OUR EVENT",
    run_title: "Social Run for Spain",
    run_body: "A run open to everyone. Speed doesn't matter. Running together does. We'll gather afterwards at Dansepladsen with live music, Spanish food and a real celebration of solidarity.",
    run_not_race: "This is not a race. It is a celebration of solidarity.",
    btn_inscribirse: "Register",
    btn_mas_info: "More info",
    event_fecha: "Date", event_lugar: "Location",
    event_distancia: "Distance", event_precio: "Price",
    timeline_kicker: "PROGRAMME",
    timeline_title: "The day we run together.",
    tl_1330: "Registration opens", tl_1330_sub: "Accreditation and bib pick-up",
    tl_1430: "Warm-up", tl_1430_sub: "Together — bodies ready",
    tl_1500: "5 km Social Run starts", tl_1500_sub: "Fælledparken, Copenhagen",
    tl_1600: "Finish at Dansepladsen", tl_1600_sub: "We gather at the end of the route",
    tl_1615: "Live music begins", tl_1615_sub: "The celebration starts",
    tl_1700: "Spanish food & drinks", tl_1700_sub: "Food and drinks for everyone",
    tl_1800: "Charity programme", tl_1800_sub: "Stories, thank-yous, auction",
    tl_2200: "Event closes", tl_2200_sub: "Thank you for making it possible",
    counter_kicker: "OUR IMPACT",
    counter_title: "Numbers that are people.",
    counter_raised: "DKK raised", counter_participants: "Participants",
    counter_volunteers: "Volunteers", counter_partners: "Partners",
    partners_kicker: "THANK YOU", partners_title: "Who make this movement possible.",
    partners_main: "Main partners",
    partners_supporting: "Supporting partners",
    partners_community: "Community partners",
    partners_media: "Media partners",
    partners_become: "Want to join?",
    partners_become_cta: "Contact partnerships",
    partners_soon: "Coming soon",
    map_kicker: "EVENT GUIDE",
    map_title: "Everything you need to know, on the ground.",
    map_1: "Start area", map_2: "5 km route", map_3: "Finish",
    map_4: "Dansepladsen", map_5: "Food area", map_6: "Stage",
    map_7: "Sponsor village", map_8: "Toilets", map_9: "Information point",
    map_10: "First aid",
    stories_title: "Stories of solidarity",
    stories_kicker: "STORIES",
    story_1_t: "Behind the Social Run",
    story_1_e: "The route, the teams and the last details of the day.",
    story_2_t: "New sponsors",
    story_2_e: "Companies from Denmark and Spain joining the movement this year.",
    story_3_t: "Our volunteers",
    story_3_e: "The people behind every event — their faces, their reasons.",
    story_4_t: "The solidarity truck to Valencia",
    story_4_e: "Snow in Copenhagen, a full trailer, and families waiting in Valencia.",
    story_5_t: "The jersey auction",
    story_5_e: "Shirts signed by football legends for a bigger cause.",
    ver_todas: "See all stories",
    news_title: "Stay connected.",
    news_sub: "Receive news, upcoming events and stories about the impact we create together.",
    news_signup: "Sign up", news_placeholder: "Your email",
    footer_cta_title: "Together across borders.",
    footer_cta_body: "From Denmark, with a heart in Spain.",
    footer_cta_btn: "Join the movement",
    float_join: "Join", float_join_mobile: "Join",
    sponsor_thanks_kicker: "THANK YOU",
    sponsor_thanks_title: "We'll be in touch very soon.",
    sponsor_thanks_body: "In the meantime, download our Partnership Book — discover the opportunities, reach and activations we offer.",
    sponsor_download: "Download Partnership Book (PDF)",
    sponsor_download_soon: "Book available soon",
    ss_registered: "Runners registered", ss_raised: "Raised", ss_families: "Families supported",
    trust_ssl: "Encrypted payment", trust_cvr: "CVR-registered charity",
    trust_deduct: "", trust_audit: "Audited annually",
    footer_tagline: "From Copenhagen to Spain, we build lasting connections.",
    footer_links: "Explore", footer_legal: "Legal", footer_transparencia: "Transparency",
    footer_contact: "Contact", footer_closing: "Made with heart in Copenhagen",
    footer_donation_info: "Donation information",
    donate_headline: "Your donation arrives — directly.",
    donate_sub: "No middlemen. 92 øre of every krone reaches the field.",
    amount_label: "Choose amount (DKK)",
    custom_amount: "Custom amount",
    monthly_soon: "Monthly — DANAmark Circle · launching soon",
    proceed: "Continue to payment",
    thank_you_t: "Thank you — heart to heart.", thank_you_sub: "Your receipt is on its way to your inbox.",
    read_more: "Read more", back: "Back",
    countdown_days: "days", countdown_hours: "hours", countdown_min: "min", countdown_sec: "sec",
    admin_login: "Editor login", email: "Email", password: "Password", sign_in: "Sign in",
    cookie_msg: "We use essential cookies to run the site. You choose the rest.",
    cookie_accept: "Accept all", cookie_essential: "Essential only",
    volunteer_title: "Become a volunteer.",
    volunteer_sub: "We meet through movement. Many hands, one heart.",
    volunteer_interests: "Where would you like to help?",
    volunteer_availability: "Your availability",
    volunteer_message: "Tell us about yourself",
    volunteer_send: "Send",
    volunteer_ok: "Thank you — we'll reply within 5 working days.",
    pay_mobilepay: "Pay with MobilePay Box",
    pay_stripe: "Card · Apple Pay · Google Pay",
    pay_mp_pending: "MobilePay Box activates when credentials arrive",
    nav_social_run: "Social Run",
    nav_partners: "Partners",
    nav_contact: "Contact",
    close_menu: "Close",
    menu_join_run: "Join the Social Run",
    event_quick_kicker: "OUR EVENT",
    event_line_date: "6 September 2026",
    event_line_where: "Fælledparken · Copenhagen",
    event_line_dist: "5 km Social Run",
    event_line_finish: "Finish at Dansepladsen",
    event_line_music: "Live music",
    event_line_food: "Spanish gastronomy",
    event_line_celebration: "Community celebration",
  },
  da: {
    nav_mission: "Vores historie", nav_events: "Social Run", nav_impact: "Vores aftryk",
    nav_stories: "Historier", nav_about: "Om os", nav_get_involved: "Bliv en del af fællesskabet",
    nav_home: "Forside", nav_register_cta: "Tilmeld dig",
    nav_volunteer: "Frivillig",
    donate: "Donér", donate_now: "Donér nu", register: "Tilmeld",
    nav_raffle_cta: "Køb lodder",
    nav_raffle: "LODTRÆKNING", nav_raffle_sub: "VIND PRÆMIER",
    hero_eyebrow: "FRA DANMARK, MED SOLIDARITET",
    hero_title: "Når Spanien har brug for os, står vi sammen.",
    hero_sub: "Fra København til Spanien finder solidariteten sin vej. DANAmark Solidaritet forbinder mennesker, lokalsamfund og virksomheder for at omsætte solidaritet til reel hjælp, når Spanien har mest brug for det.",
    cta_participa: "Tilmeld dig",
    cta_conoce: "Vores historie",
    bar_date: "Søndag 6. september",
    bar_place: "Fælledparken",
    bar_dist: "5 km",
    bar_music: "Live musik",
    bar_food: "Spansk mad",
    bar_charity: "Velgørende event",
    section_historia_kicker: "VORES HISTORIE",
    historia_headline: "Det hele begyndte med en beslutning.",
    historia_body_1: "I efteråret 2024 efterlod DANA-oversvømmelserne tusindvis af familier ramt i Valencia. Selvom vi var mere end to tusinde kilometer væk, kunne vi ikke bare stå og se på.",
    historia_body_2: "En gruppe spanske og danske frivillige besluttede at arrangere solidaritetsarrangementer i København. Sådan blev DANAmark Solidaritet født.",
    historia_body_3: "Det, der begyndte som en akut reaktion, blev en bevægelse, der beviser, at solidaritet ikke kender grænser.",
    section_conseguimos: "HVAD VI HAR OPNÅET SAMMEN",
    card1_title: "Over 200.000 DKK indsamlet.",
    card1_body: "Takket være hundredvis af menneskers og virksomheders solidaritet i Danmark.",
    card2_title: "Solidaritetsarrangementer og velgørenhedsauktioner.",
    card2_body: "Koncerter, kulturaktiviteter og auktioner, der samlede fællesskabet om én sag.",
    card3_title: "En lastbil med hjælp sendt til Valencia.",
    card3_body: "Vi organiserede indsamling og forsendelse af møbler og materialer til DANA-ramte familier.",
    card4_title: "Et fællesskab, der bliver ved med at vokse.",
    card4_body: "Hver frivillig, samarbejdspartner og sponsor beviser, at solidaritet ikke kender grænser.",
    section_hoy: "I DAG LØBER VI IGEN FOR SPANIEN",
    hoy_body: "I 2026 har skovbrandene ødelagt tusindvis af hektarer og tvunget mange familier til at forlade deres hjem. Vi vil bevise igen, at solidaritet rejser hurtigere end enhver grænse.",
    hoy_icon_1: "Hver tilmelding.",
    hoy_icon_2: "Hver sponsor.",
    hoy_icon_3: "Hver frivillig.",
    hoy_icon_4: "Hver donation.",
    hoy_final: "Bliver til rigtig hjælp.",
    run_kicker: "VORES BEGIVENHED",
    run_title: "Social Run for Spain",
    run_body: "Et løb åbent for alle. Farten er ikke det vigtige. At løbe sammen er. Vi mødes bagefter på Dansepladsen med live musik, spansk mad og en ægte solidaritetsfejring.",
    run_not_race: "Det er ikke et race. Det er en fejring af solidaritet.",
    btn_inscribirse: "Tilmeld",
    btn_mas_info: "Mere info",
    event_fecha: "Dato", event_lugar: "By",
    event_distancia: "Distance", event_precio: "Pris",
    timeline_kicker: "PROGRAM",
    timeline_title: "Dagen vi løber sammen.",
    tl_1330: "Tilmelding åbner", tl_1330_sub: "Akkreditering og startnummer",
    tl_1430: "Opvarmning", tl_1430_sub: "Sammen — kroppen klar",
    tl_1500: "Start — 5 km Social Run", tl_1500_sub: "Fælledparken, København",
    tl_1600: "Mål på Dansepladsen", tl_1600_sub: "Vi mødes ved rutens slutning",
    tl_1615: "Live musik starter", tl_1615_sub: "Festen begynder",
    tl_1700: "Spansk mad & drikke", tl_1700_sub: "Mad og drikke til alle",
    tl_1800: "Velgørenhedsprogram", tl_1800_sub: "Historier, tak, auktion",
    tl_2200: "Eventet slutter", tl_2200_sub: "Tak fordi I gør det muligt",
    counter_kicker: "VORES EFFEKT",
    counter_title: "Tal, der er mennesker.",
    counter_raised: "DKK indsamlet", counter_participants: "Deltagere",
    counter_volunteers: "Frivillige", counter_partners: "Sponsorer",
    partners_kicker: "TAK", partners_title: "Dem der gør bevægelsen mulig.",
    partners_main: "Hovedsponsorer",
    partners_supporting: "Støttende partnere",
    partners_community: "Community partnere",
    partners_media: "Medier",
    partners_become: "Vil du være med?",
    partners_become_cta: "Kontakt partnerskaber",
    partners_soon: "Kommer snart",
    map_kicker: "EVENT GUIDE",
    map_title: "Alt du skal vide, på stedet.",
    map_1: "Start-område", map_2: "5 km rute", map_3: "Mål",
    map_4: "Dansepladsen", map_5: "Mad-område", map_6: "Scene",
    map_7: "Sponsor village", map_8: "Toiletter", map_9: "Informationspunkt",
    map_10: "Førstehjælp",
    stories_title: "Historier om solidaritet",
    stories_kicker: "HISTORIER",
    story_1_t: "Bag Social Run",
    story_1_e: "Ruten, holdene og de sidste detaljer inden dagen.",
    story_2_t: "Nye sponsorer",
    story_2_e: "Virksomheder fra Danmark og Spanien, der er med i år.",
    story_3_t: "Vores frivillige",
    story_3_e: "Menneskene bag hvert arrangement — deres ansigter, deres grunde.",
    story_4_t: "Solidaritetslastbilen til Valencia",
    story_4_e: "Sne i København, et fyldt lad, og familier der venter i Valencia.",
    story_5_t: "Trøje-auktionen",
    story_5_e: "Trøjer signeret af fodboldlegender for en større sag.",
    ver_todas: "Se alle historier",
    news_title: "Hold kontakten.",
    news_sub: "Modtag nyheder, kommende arrangementer og historier om den effekt vi skaber sammen.",
    news_signup: "Skriv dig op", news_placeholder: "Din e-mail",
    footer_cta_title: "Sammen på tværs af grænser.",
    footer_cta_body: "Fra Danmark, med hjertet i Spanien.",
    footer_cta_btn: "Deltag i bevægelsen",
    float_join: "Vær med", float_join_mobile: "Vær med",
    sponsor_thanks_kicker: "TAK",
    sponsor_thanks_title: "Vi vender tilbage meget snart.",
    sponsor_thanks_body: "I mellemtiden kan du downloade vores Partnership Book og se de muligheder, den rækkevidde og de aktiveringer vi tilbyder.",
    sponsor_download: "Download Partnership Book (PDF)",
    sponsor_download_soon: "Bogen tilgængelig snart",
    ss_registered: "Løbere tilmeldt", ss_raised: "Indsamlet", ss_families: "Familier støttet",
    trust_ssl: "Krypteret betaling", trust_cvr: "CVR-registreret forening",
    trust_deduct: "", trust_audit: "Revideret årligt",
    footer_tagline: "Fra København til Spanien bygger vi varige forbindelser.",
    footer_links: "Udforsk", footer_legal: "Juridisk", footer_transparencia: "Åbenhed",
    footer_contact: "Kontakt", footer_closing: "Bygget med hjerte i København",
    footer_donation_info: "Donationsinformation",
    donate_headline: "Din donation når frem — direkte.",
    donate_sub: "Ingen mellemled. 92 øre af hver krone går videre.",
    amount_label: "Vælg beløb (DKK)",
    custom_amount: "Andet beløb",
    monthly_soon: "Månedlig — DANAmark Circle · lanceres snart",
    proceed: "Fortsæt til betaling",
    thank_you_t: "Tak — fra hjerte til hjerte.", thank_you_sub: "Din kvittering er på vej til din indbakke.",
    read_more: "Læs mere", back: "Tilbage",
    countdown_days: "dage", countdown_hours: "timer", countdown_min: "min", countdown_sec: "sek",
    admin_login: "Editor-login", email: "E-mail", password: "Adgangskode", sign_in: "Log ind",
    cookie_msg: "Vi bruger nødvendige cookies for at drive siden. Vælg selv resten.",
    cookie_accept: "Accepter alle", cookie_essential: "Kun nødvendige",
    volunteer_title: "Bliv frivillig.",
    volunteer_sub: "Vi mødes gennem bevægelse. Mange hænder, ét hjerte.",
    volunteer_interests: "Hvor vil du hjælpe?",
    volunteer_availability: "Din tilgængelighed",
    volunteer_message: "Fortæl os om dig selv",
    volunteer_send: "Send",
    volunteer_ok: "Tak — vi vender tilbage inden 5 hverdage.",
    pay_mobilepay: "Betal med MobilePay Box",
    pay_stripe: "Kort · Apple Pay · Google Pay",
    pay_mp_pending: "MobilePay Box aktiveres når kreditiver er klar",
    nav_social_run: "Social Run",
    nav_partners: "Partnere",
    nav_contact: "Kontakt",
    close_menu: "Luk",
    menu_join_run: "Deltag i Social Run",
    event_quick_kicker: "VORES BEGIVENHED",
    event_line_date: "6. september 2026",
    event_line_where: "Fælledparken · København",
    event_line_dist: "5 km Social Run",
    event_line_finish: "Mål på Dansepladsen",
    event_line_music: "Live musik",
    event_line_food: "Spansk gastronomi",
    event_line_celebration: "Fællesskabsfejring",
  },
};

const Ctx = createContext(null);
export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("dana_lang");
    if (saved && dict[saved]) return saved;
    // Danish is the product default; only respect browser locale when it is explicitly ES or EN.
    const nav = (navigator.language || "").toLowerCase();
    if (nav.startsWith("es")) return "es";
    if (nav.startsWith("en")) return "en";
    return "da";
  });
  useEffect(() => { localStorage.setItem("dana_lang", lang); document.documentElement.lang = lang; }, [lang]);
  const t = (key) => (dict[lang] && dict[lang][key]) || dict.en[key] || key;
  const pick = (obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    return obj[lang] || obj.es || obj.en || obj.da || Object.values(obj)[0] || "";
  };
  return <Ctx.Provider value={{ lang, setLang, t, pick }}>{children}</Ctx.Provider>;
}
export const useI18n = () => useContext(Ctx);
